import { Component, DestroyRef, effect, inject, input, model, OnDestroy, OnInit } from '@angular/core';

import { ButtonModule, IconModule } from '@hglobal/fin-ui';
import { FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule } from '@ngx-translate/core';

import { CopyTooltipComponent, WalletCardComponent } from '@kr/components';
import { ExchangeStore } from '@kr/components/exchange/exchange.store';
import { ExchangeForm } from '@kr/components/exchange/models';
import { isUsdtSymbol, WalletCard } from '@kr/models';
import { CurrencyBalanceFormatPipe } from '@kr/pipes';

@Component({
  selector: 'kr-exchange-crypto-details',
  imports: [IconModule, WalletCardComponent, TranslateModule, ButtonModule, CurrencyBalanceFormatPipe, CopyTooltipComponent],
  templateUrl: './exchange-crypto-details.component.html',
  styleUrl: './exchange-crypto-details.component.scss',
})
export class ExchangeCryptoDetailsComponent implements OnInit, OnDestroy {
  #duration = 60;
  #step = 1000;
  #intervalId: any = null;
  #endTime: number;

  readonly destroyRef = inject(DestroyRef);
  readonly store = inject(ExchangeStore);

  form = input<FormGroup<GroupResolverFormBuilder<ExchangeForm>>>();
  wallets = input<WalletCard[]>();
  estimation = this.store.exchangeEstimate;

  walletFrom: WalletCard;
  walletTo: WalletCard;

  timeLeft = model(this.#duration);

  constructor() {
    effect(() => {
      if (this.estimation()) {
        this.startTimer();
      }
    });
  }

  isUsdt(symbol: string): string {
    return String(isUsdtSymbol(symbol));
  }

  ngOnInit(): void {
    this.walletFrom = this.wallets().find(wallet => wallet.id == this.form().value.walletFromId.toString());
    this.walletTo = this.wallets().find(wallet => wallet.id == this.form().value.walletToId.toString());

    this.store.saveWalletOrgId(this.walletFrom.organizationId);

    this.getEstimation();
  }

  startTimer() {
    if (this.#intervalId) {
      return;
    }

    const now = Date.now();
    this.#endTime = now + this.#duration * 1000;

    this.updateTimeLeft();
    this.#intervalId = setInterval(() => {
      this.updateTimeLeft();
    }, this.#step);
  }

  updateTimeLeft() {
    const now = Date.now();
    const secondsLeft = Math.max(0, Math.floor((this.#endTime - now) / 1000));
    this.timeLeft.set(secondsLeft);

    if (secondsLeft === 0) {
      this.stopTimer();
    }
  }

  resetTimer() {
    this.stopTimer();
    this.store.clearEstimate();
    this.getEstimation();
  }

  stopTimer() {
    if (this.#intervalId !== null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  getEstimation(): void {
    this.store.getExchangeEstimate({
      walletFromId: this.form().value.walletFromId.toString(),
      walletToId: this.form().value.walletToId.toString(),
      amount: this.form().value.amount || this.form().value.amountTo,
      toReceive: !!this.form().value.amountTo,
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }
}
